import { Phone } from "lucide-react";
import { ALCHAMIS_PHONES, FlagIcon } from "@/components/FlagIcon";

/**
 * GlobalContactBlock
 * Renders the canonical Alchamis international phone list with flag icons.
 *
 * Variants:
 *   - "footer"  : compact, dimmer text, vertical list
 *   - "card"    : individual bordered card per number (used on Contact/About)
 *   - "stack"   : tight vertical list inside a single bordered card
 */
export default function GlobalContactBlock({ variant = "card", testIdPrefix = "global-contact" }) {
  if (variant === "footer") {
    return (
      <ul className="space-y-2.5 text-sm text-white/65">
        {ALCHAMIS_PHONES.map((p) => (
          <li key={p.tel} className="flex items-center gap-3" data-testid={`${testIdPrefix}-${p.tel}`}>
            <FlagIcon country={p.country} size={14} />
            <a
              href={`tel:${p.tel}`}
              className="hover:text-white transition-colors tracking-wide"
              aria-label={`Call Alchamis ${p.label} office at ${p.display}`}
            >
              {p.display}
            </a>
            <span className="text-[0.6rem] tracking-[0.22em] uppercase text-white/35">{p.label}</span>
          </li>
        ))}
      </ul>
    );
  }

  if (variant === "stack") {
    return (
      <div className="border border-white/10 divide-y divide-white/5">
        <div className="px-5 py-3 flex items-center gap-3">
          <Phone className="text-gold" size={16} />
          <span className="overline">Global Lines</span>
        </div>
        {ALCHAMIS_PHONES.map((p) => (
          <a
            key={p.tel}
            href={`tel:${p.tel}`}
            data-testid={`${testIdPrefix}-${p.tel}`}
            className="flex items-center gap-4 px-5 py-3.5 hover:bg-white/[0.03] transition-colors group"
            aria-label={`Call Alchamis ${p.label} office at ${p.display}`}
          >
            <FlagIcon country={p.country} size={16} />
            <span className="text-white group-hover:text-gold transition-colors tracking-wide text-sm">
              {p.display}
            </span>
            <span className="ml-auto text-[0.6rem] tracking-[0.22em] uppercase text-white/45">
              {p.label}
            </span>
          </a>
        ))}
      </div>
    );
  }

  // default "card"
  return (
    <div className="space-y-3">
      {ALCHAMIS_PHONES.map((p) => (
        <a
          key={p.tel}
          href={`tel:${p.tel}`}
          data-testid={`${testIdPrefix}-${p.tel}`}
          className="flex items-center gap-4 p-4 border border-white/10 hover:border-gold/40 hover:bg-white/[0.02] transition-all group"
          aria-label={`Call Alchamis ${p.label} office at ${p.display}`}
        >
          <FlagIcon country={p.country} size={18} />
          <div className="flex-1 min-w-0">
            <div className="text-white group-hover:text-gold transition-colors tracking-wide text-sm">
              {p.display}
            </div>
            <div className="text-[0.6rem] tracking-[0.22em] uppercase text-white/45 mt-1">
              {p.label}
            </div>
          </div>
          <Phone className="text-gold/60 group-hover:text-gold transition-colors" size={14} />
        </a>
      ))}
    </div>
  );
}
